import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    ScrollTable,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsTopBuyersApi } from "../../bidsApi";
import { BidTopBuyerInfo } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks } from "../format";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function TopBuyersTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidTopBuyerInfo[]>(
        () =>
            bidsTopBuyersApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                take: filters.take,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, filters.take]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showTake
            />

            <AnalyticsCard
                title="Топ покупателей по заявкам"
                subtitle="VIP, концентрация спроса и аномалии покупателей."
                snapshotLabel="period"
                howToRead={
                    <Typography level="body-sm">
                        Топ по сумме остатка. Сравнивайте остаток и выкупили. Клик по имени — карточка
                        пользователя, по Steam ID — список заявок с фильтром (открываются в новой
                        вкладке).
                    </Typography>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Доля топа у 1–2 покупателей",
                                growsIsGood: false,
                                ifGrows: "зависимость от VIP — уход одного сильно ударит по спросу.",
                                ifFalls: "спрос распределён по многим покупателям — устойчивее.",
                            },
                            {
                                signal: "Выкуп относительно остатка у покупателя",
                                ifGrows: "остаток невелик, выкупили много — покупатель реально закрывает спрос.",
                                ifFalls: "покупатель держит большой остаток без покупок — проверить баланс, цены, отключение заявок.",
                            },
                            {
                                signal: "Сумма спроса у топа",
                                ifGrows: "рост с выкупом — активный платёжеспособный клиент; без выкупа — копятся неисполненные VIP-заявки.",
                                ifFalls: "крупные покупатели стали меньше ставить заявки.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && (!state.data || state.data.length === 0) && (
                    <EmptyState />
                )}
                {!state.loading && !state.error && state.data && state.data.length > 0 && (
                    <ScrollTable>
                        <Table size="sm" stickyHeader sx={{ minWidth: 760 }}>
                            <thead>
                                <tr>
                                    <th>Покупатель</th>
                                    <th>Steam ID</th>
                                    <th>Заявок</th>
                                    <th>Сумма спроса</th>
                                    <th>Остаток</th>
                                    <th>Выкупили</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.data.map((row) => (
                                    <tr key={String(row.userId)}>
                                        <td>
                                            <Link
                                                level="body-sm"
                                                href={`/app/users/${String(row.userId)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {row.buyerName || "—"}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link
                                                level="body-sm"
                                                href={`/app/bids?userId=${String(row.userId)}#list`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {String(row.userId)}
                                            </Link>
                                        </td>
                                        <td>{formatInt(row.bidsCount)}</td>
                                        <td>{formatMoneyKopecks(row.totalSum)}</td>
                                        <td>{formatInt(row.totalCount)}</td>
                                        <td>{formatInt(row.totalBought)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ScrollTable>
                )}
            </AnalyticsCard>
        </Box>
    );
}
